import { differenceInCalendarDays } from "date-fns";
import { numberToHtmlColor } from "@archipad-js/archipad/utils";

import { WorkPackageEntity } from "../../models/EntityClasses";

export enum ColorFunction {
    Monochrome,
    TimeBased,
    StatusBased,
    WorkPackages,
    Undefined,
}

export const enum ColorScheme {
    All = "#00FF00",
    Black = "#444444",
    Blue = "#0051BA",
    Green = "#2E7D32",
    Orange = "#F96302",
    Red = "#CE1326",
    Gray = "#999999", // US spelling for consistency (grey in UK)
}

export abstract class BaseColorContext {
    /**
     * The duration in days after which something is considered "hot"
     *
     * Example: orange warning when something is getting close to be late.
     */
    protected hotDurationInDays = 0;

    /**
     * The duration in days after which something created x days ago is
     * considered "late" even if there is no due date.
     *
     * Example: red warning when something with no due date has been created x
     * days ago.
     */
    protected lateAfterEditableCreationDateDurationInDays = 0;

    constructor(protected readonly originDateTime: Date) {}

    public static parseColorFunction(rawColorFunction: string): ColorFunction {
        switch (rawColorFunction) {
            case "monochrome":
                return ColorFunction.Monochrome;
            case "time_based":
                return ColorFunction.TimeBased;
            case "status_based":
                return ColorFunction.StatusBased;
            case "wps":
                return ColorFunction.WorkPackages;
            default:
                return ColorFunction.Undefined;
        }
    }

    protected getColorUsingDates(creationDate: Date | null, dueDate?: Date | null): string {
        if (!creationDate) {
            return ColorScheme.Black;
        }

        const daysDuration = this.getDurationInDays(creationDate, dueDate);

        if (daysDuration < 0) {
            return ColorScheme.Red;
        }

        if (daysDuration < this.hotDurationInDays) {
            return ColorScheme.Orange;
        }

        return ColorScheme.Blue;
    }

    protected getColorUsingWorkPackage(workPackage: WorkPackageEntity | null): string {
        if (workPackage) {
            return numberToHtmlColor(workPackage.color);
        }

        return ColorScheme.All;
    }

    private getDurationInDays(creationDate: Date, dueDate?: Date | null): number {
        if (dueDate) {
            return differenceInCalendarDays(dueDate, this.originDateTime);
        }

        return (
            differenceInCalendarDays(creationDate, this.originDateTime) +
            this.lateAfterEditableCreationDateDurationInDays
        );
    }
}
